import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { ApiError, fetchJson } from "@/lib/api";
import type { ServiceAreaCheckResult } from "./types";

const DEBOUNCE_MS = 300;
const MIN_LENGTH = 4;

// Debounces the address through state, then defers to react-query so we
// get free request dedupe + cache. The cache key is the trimmed address —
// keystroke `123 Main` then `123 main` should hit the same row.
//
// Status mapping:
//   "" (or < MIN_LENGTH chars) -> idle  (no request fired)
//   debouncing                  -> idle  (no request fired yet)
//   in-flight                   -> "loading"
//   request done                -> "success" or "error"
export interface UseServiceAreaCheck {
  status: "idle" | "loading" | "success" | "error";
  data: ServiceAreaCheckResult | undefined;
  error: ApiError | Error | null;
  isDebouncing: boolean;
  // Bypasses the debounce window — used by the "Check availability"
  // button on the home hero so impatient users don't have to wait the
  // 300 ms after their last keystroke. Safe to call when address is
  // below MIN_LENGTH (no-op).
  flush: () => void;
}

export function useServiceAreaCheck(address: string, debounceMs: number = DEBOUNCE_MS): UseServiceAreaCheck {
  const trimmed = address.trim();
  // Initialise to "" so the first render is always below MIN_LENGTH —
  // mount + props together must go through the debounce window before
  // a request fires. Keeps initial prefill behaviour identical to typing.
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    if (trimmed === debounced) return;
    const id = setTimeout(() => setDebounced(trimmed), debounceMs);
    return () => clearTimeout(id);
  }, [trimmed, debounced, debounceMs]);

  const flush = useCallback(() => {
    setDebounced(trimmed);
  }, [trimmed]);

  const enabled = debounced.length >= MIN_LENGTH;
  const query: UseQueryResult<ServiceAreaCheckResult, ApiError | Error> = useQuery({
    queryKey: ["service_area_check", debounced.toLowerCase()],
    queryFn: () =>
      fetchJson<ServiceAreaCheckResult>(
        `/api/v1/service_area_check?address=${encodeURIComponent(debounced).replace(/%20/g, "+")}`
      ),
    enabled,
    // Geocodes are cached server-side for 30 days. We only re-fetch on
    // address change (different cache key) — never on focus.
    staleTime: 5 * 60_000,
    retry: 0
  });

  const status: UseServiceAreaCheck["status"] = !enabled
    ? "idle"
    : query.isPending
      ? "loading"
      : query.isError
        ? "error"
        : "success";

  return {
    status,
    data: query.data,
    error: query.error,
    isDebouncing: trimmed !== debounced,
    flush
  };
}
